import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface TooltipState {
  content: string;
  x: number;
  y: number;
  position: 'above' | 'below' | 'right' | 'left';
  element: HTMLElement;
}

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoveredElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Skip if element or parent has data-tooltip-skip (for custom tooltips)
      let current: HTMLElement | null = target;
      while (current) {
        if (current.hasAttribute('data-tooltip-skip')) {
          return;
        }
        current = current.parentElement;
      }

      // Check if element or any parent has a title attribute
      let elementWithTitle: HTMLElement | null = null;
      let titleText: string | null = null;

      current = target;
      while (current && current !== document.body) {
        if (current.hasAttribute('title') && current.getAttribute('title')) {
          elementWithTitle = current;
          titleText = current.getAttribute('title');
          break;
        }
        current = current.parentElement;
      }

      if (!elementWithTitle || !titleText) {
        // If we're leaving the element with title, hide tooltip
        if (hoveredElementRef.current && !hoveredElementRef.current.contains(target)) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          const originalTitle = hoveredElementRef.current.getAttribute('data-original-title');
          if (originalTitle) {
            hoveredElementRef.current.setAttribute('title', originalTitle);
            hoveredElementRef.current.removeAttribute('data-original-title');
          }
          hoveredElementRef.current = null;
          setTooltip(null);
        }
        return;
      }

      // If we're already showing tooltip for this element, don't do anything
      if (hoveredElementRef.current === elementWithTitle) {
        return;
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Restore previous element's title if needed
      if (hoveredElementRef.current && hoveredElementRef.current !== elementWithTitle) {
        const prevTitle = hoveredElementRef.current.getAttribute('data-original-title');
        if (prevTitle) {
          hoveredElementRef.current.setAttribute('title', prevTitle);
          hoveredElementRef.current.removeAttribute('data-original-title');
        }
      }

      // Store reference to hovered element
      hoveredElementRef.current = elementWithTitle;

      // Temporarily remove title to prevent browser tooltip
      const originalTitle = titleText;
      elementWithTitle.removeAttribute('title');
      elementWithTitle.setAttribute('data-original-title', originalTitle);

      // Calculate position with smart positioning
      const rect = elementWithTitle.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Estimate tooltip size (more conservative estimates)
      const tooltipPadding = 20; // 10px padding on each side
      const tooltipArrowHeight = 10;
      const tooltipSpacing = 10; // Space between element and tooltip
      // More accurate width estimate (accounting for max-width of 500px)
      const estimatedTooltipWidth = Math.min(500, Math.max(100, originalTitle.length * 6.5 + tooltipPadding));
      // Height estimate (accounting for multi-line text)
      const lines = Math.ceil(originalTitle.length / 40); // Roughly 40 chars per line
      const estimatedTooltipHeight = Math.max(30, lines * 20) + tooltipPadding;
      
      let position: 'above' | 'below' | 'right' | 'left' = 'above';
      let x = rect.left + rect.width / 2;
      let y = rect.top;
      
      // Check if tooltip fits above
      const spaceAbove = rect.top;
      const neededAbove = estimatedTooltipHeight + tooltipArrowHeight + tooltipSpacing;
      const fitsAbove = spaceAbove >= neededAbove;
      
      // Also check if tooltip would go off left/right when centered above
      const halfTooltipWidth = estimatedTooltipWidth / 2;
      const fitsHorizontallyAbove = (x - halfTooltipWidth >= 0) && (x + halfTooltipWidth <= viewportWidth);
      
      if (!fitsAbove || !fitsHorizontallyAbove) {
        // Try below
        const spaceBelow = viewportHeight - rect.bottom;
        const neededBelow = estimatedTooltipHeight + tooltipArrowHeight + tooltipSpacing;
        const fitsHorizontallyBelow = (x - halfTooltipWidth >= 0) && (x + halfTooltipWidth <= viewportWidth);
        
        if (spaceBelow >= neededBelow && fitsHorizontallyBelow) {
          position = 'below';
          y = rect.bottom;
        } else {
          // Try right
          const spaceRight = viewportWidth - rect.right;
          const neededRight = estimatedTooltipWidth + tooltipArrowHeight + tooltipSpacing;
          const fitsVerticallyRight = (rect.top + rect.height / 2 - estimatedTooltipHeight / 2 >= 0) && 
                                      (rect.top + rect.height / 2 + estimatedTooltipHeight / 2 <= viewportHeight);
          
          if (spaceRight >= neededRight && fitsVerticallyRight) {
            position = 'right';
            x = rect.right;
            y = rect.top + rect.height / 2;
          } else {
            // Try left
            const spaceLeft = rect.left;
            const neededLeft = estimatedTooltipWidth + tooltipArrowHeight + tooltipSpacing;
            const fitsVerticallyLeft = (rect.top + rect.height / 2 - estimatedTooltipHeight / 2 >= 0) && 
                                       (rect.top + rect.height / 2 + estimatedTooltipHeight / 2 <= viewportHeight);
            
            if (spaceLeft >= neededLeft && fitsVerticallyLeft) {
              position = 'left';
              x = rect.left;
              y = rect.top + rect.height / 2;
            } else {
              // Fallback to below even if tight (better than off-screen)
              position = 'below';
              y = rect.bottom;
            }
          }
        }
      }

      // Set timeout for 0.1s
      timeoutRef.current = setTimeout(() => {
        setTooltip({
          content: originalTitle,
          x,
          y,
          position,
          element: elementWithTitle!,
        });
      }, 100);
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      
      // Only hide if we're actually leaving the element with title
      if (hoveredElementRef.current) {
        // Check if we're leaving the element (relatedTarget is null or not contained in the element)
        const isLeavingElement = !relatedTarget || !hoveredElementRef.current.contains(relatedTarget);
        const isTargetInElement = target === hoveredElementRef.current || hoveredElementRef.current.contains(target);
        
        if (isLeavingElement && isTargetInElement) {
          // Clear timeout if mouse leaves before 0.1s
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

          // Restore title attribute if we removed it
          const originalTitle = hoveredElementRef.current.getAttribute('data-original-title');
          if (originalTitle) {
            hoveredElementRef.current.setAttribute('title', originalTitle);
            hoveredElementRef.current.removeAttribute('data-original-title');
          }
          hoveredElementRef.current = null;

          // Hide tooltip immediately
          setTooltip(null);
        }
      }
    };

    // Use event delegation on document (mouseover/mouseout bubble)
    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mouseout', handleMouseOut, true);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Restore any remaining title attributes
      if (hoveredElementRef.current) {
        const originalTitle = hoveredElementRef.current.getAttribute('data-original-title');
        if (originalTitle) {
          hoveredElementRef.current.setAttribute('title', originalTitle);
          hoveredElementRef.current.removeAttribute('data-original-title');
        }
      }
    };
  }, []);

  return (
    <>
      {children}
      {tooltip && createPortal(
        <div
          style={{
            position: 'fixed',
            left: tooltip.position === 'left' ? `${tooltip.x - 10}px` : tooltip.position === 'right' ? `${tooltip.x + 10}px` : `${tooltip.x}px`,
            top: tooltip.position === 'above' ? `${tooltip.y - 10}px` : tooltip.position === 'below' ? `${tooltip.y + 10}px` : `${tooltip.y}px`,
            transform: 
              tooltip.position === 'above' ? 'translateX(-50%) translateY(-100%)' :
              tooltip.position === 'below' ? 'translateX(-50%)' :
              tooltip.position === 'right' ? 'translateY(-50%)' :
              'translateX(-100%) translateY(-50%)',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 99999,
            pointerEvents: 'none',
            border: '1px solid var(--border-primary)',
            maxWidth: '500px',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {tooltip.content}
          <div
            style={{
              position: 'absolute',
              ...(tooltip.position === 'above' ? {
                bottom: '-5px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: '5px solid var(--bg-secondary)',
              } : tooltip.position === 'below' ? {
                top: '-5px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderBottom: '5px solid var(--bg-secondary)',
              } : tooltip.position === 'right' ? {
                left: '-5px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderRight: '5px solid var(--bg-secondary)',
              } : {
                right: '-5px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderLeft: '5px solid var(--bg-secondary)',
              }),
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
};

